import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import DashboardHeader from "@/components/DashboardHeader";
import AppFooter from "@/components/AppFooter";
import ScrollToTop from "@/components/ScrollToTop";
import FoodDatabaseManager from "@/components/FoodDatabaseManager";
import FoodSuggestionsManager from "@/components/admin/FoodSuggestionsManager";
import NutramiManager from "@/components/admin/NutramiManager";
import AdminAmbassadors from "@/components/AdminAmbassadors";  // iter395
import AdvisoryCOIPanel from "@/components/admin/AdvisoryCOIPanel";  // iter543
import UsdaFoodsManager from "@/components/admin/UsdaFoodsManager";
import DrugRegistryManager from "@/components/admin/DrugRegistryManager";
import ArticlesManager from "@/components/ArticlesManager";
import LandingCmsEditor from "@/components/LandingCmsEditor";
import SeoManager from "@/components/admin/SeoManager";
import ClinicianVerification from "@/components/ClinicianVerification";
import AdminUsersPanel from "@/components/admin/AdminUsersPanel";
import AdminAnalysesPanel from "@/components/admin/AdminAnalysesPanel";
import AdminSecurityPanel from "@/components/admin/AdminSecurityPanel";
import ComplianceCenterWidget from "@/components/admin/ComplianceCenterWidget";
import FormulaAuditLog from "@/components/admin/FormulaAuditLog";
import SeoAuditPanel from "@/components/admin/SeoAuditPanel";
import TrialAnalyticsPanel from "@/components/admin/TrialAnalyticsPanel";
import ClinicianTrialRequestsPanel from "@/components/admin/ClinicianTrialRequestsPanel";
import RiskStatsPanel from "@/components/admin/RiskStatsPanel";
import AdminPromoCodes from "@/components/admin/AdminPromoCodes";
import AdminFAQ from "@/components/admin/AdminFAQ";
import AdminHealthDashboard from "@/components/admin/AdminHealthDashboard";
import InterRaterStudyPanel from "@/components/admin/InterRaterStudyPanel";
import BgCalibrationPanel from "@/components/admin/BgCalibrationPanel";
import BatchPilotRunner from "@/components/admin/BatchPilotRunner";
import MolliePaymentsPanel from "@/components/admin/MolliePaymentsPanel";
import OncologyVerificationsPanel from "@/components/admin/OncologyVerificationsPanel";
import SiteKnowledgePanel from "@/components/admin/SiteKnowledgePanel";
import AdminCookbookPanel from "@/components/admin/AdminCookbookPanel";  // iter595
import AdminBroadcastsPanel from "@/components/admin/AdminBroadcastsPanel";  // iter687
import AdminMarkerReports from "@/components/admin/AdminMarkerReports";  // iter899
import AdminUnmappedConditions from "@/components/admin/AdminUnmappedConditions";  // iter899
import {
  Users, Activity, Clock, Sparkles, Target, MessageSquare, Loader2,
  BookOpen, Database, FileText, FlaskConical, Stethoscope, Globe, ShieldCheck,
  TrendingUp, Ticket, HelpCircle, HeartPulse,
  ScanEye, Scale, Upload, Microscope, History, ShieldAlert, ChefHat, Send,
  Flag, GitBranch,
} from "lucide-react";
import { format } from "date-fns";

const STAT_CARDS = [
  { key: "total", label: "admin.total_users", color: "#0EA5E9", icon: Users },
  { key: "withProfile", label: "admin.with_profile", color: "#10B981", icon: Activity },
  { key: "trialing", label: "Trial", color: "#3B82F6", icon: Clock, rawLabel: true },
  { key: "active", label: "Active", color: "#10B981", icon: Sparkles, rawLabel: true },
  { key: "expired", label: "Expired", color: "#EF4444", icon: Target, rawLabel: true },
  { key: "totalQueries", label: "admin.total_queries", color: "#6366F1", icon: MessageSquare },
];

const TABS = [
  { key: "users", label: "Users", icon: Users },
  { key: "clinicians", label: "Clinicians", icon: Stethoscope },
  { key: "onco_verify", label: "Onco Verify", icon: Microscope },
  { key: "clin_trials", label: "Clinician Trials", icon: Stethoscope },
  { key: "risk_stats", label: "Risk Stats", icon: TrendingUp },
  { key: "trials", label: "Trials", icon: TrendingUp },
  { key: "analyses", label: "Analyses", icon: FlaskConical },
  { key: "foods", label: "Food DB", icon: Database },
  { key: "nutrami", label: "Nutrami", icon: Sparkles },
  { key: "food_suggestions", label: "Food Suggestions", icon: Sparkles },
  { key: "usda_foods", label: "USDA DB (23k)", icon: Database },
  { key: "drug_registry", label: "Лекарствен указател", icon: Database },
  { key: "articles", label: "Articles", icon: BookOpen },
  { key: "cookbook", label: "Cookbook", icon: ChefHat },  // iter595
  { key: "broadcasts", label: "📣 Broadcasts", icon: Send },  // iter687
  { key: "landing", label: "Начална страница", icon: FileText },
  { key: "faq", label: "FAQ", icon: HelpCircle },
  { key: "promo", label: "Promo codes", icon: Ticket },
  { key: "ambassadors", label: "Ambassadors", icon: Sparkles },
  { key: "advisory_coi", label: "Advisory COI", icon: ShieldAlert },
  { key: "seo", label: "SEO", icon: Globe },
  { key: "security", label: "Security", icon: ShieldCheck },
  { key: "compliance", label: "Compliance", icon: ShieldCheck },
  { key: "formula_audit", label: "Formula Audit", icon: History },
  { key: "seo_audit", label: "SEO & Perf", icon: Activity },
  { key: "health", label: "Health", icon: HeartPulse },
  { key: "inter_rater", label: "Inter-rater", icon: ScanEye },
  { key: "batch_pilot", label: "Batch pilot", icon: Upload },
  { key: "mollie", label: "Mollie", icon: Ticket },
  { key: "bg_calib", label: "BG Calibration", icon: Scale },
  { key: "site_kb", label: "Site KB", icon: Globe },
  { key: "marker_reports", label: "🚩 Marker Reports", icon: Flag },  // iter899
  { key: "unmapped_kegg", label: "🧬 Unmapped KEGG", icon: GitBranch },  // iter899
];

export default function AdminPanel() {
  const { API, user } = useAuth();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ total: 0, withProfile: 0, totalQueries: 0, trialing: 0, expired: 0, active: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userQueries, setUserQueries] = useState([]);
  const [financialAudit, setFinancialAudit] = useState(null);  // iter532
  const [activeTab, setActiveTab] = useState("users");
  const [articles, setArticles] = useState([]);
  const [editArticle, setEditArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({ title: "", title_bg: "", slug: "", excerpt: "", excerpt_bg: "", content: "", content_bg: "", category: "general", published: false, tags: [] });
  const [creditAmount, setCreditAmount] = useState("");
  const [giftPlan, setGiftPlan] = useState("core");
  const [giftMonths, setGiftMonths] = useState("1");
  const [giftLoading, setGiftLoading] = useState(false);

  const fetchArticles = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/articles?published_only=false`);
      setArticles(data.articles || []);
    } catch (e) { /* silent */ }
  }, [API]);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`);
      setUsers(data.users || []);
    } catch (e) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  }, [API]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stats`);
      setStats(data);
    } catch (e) { /* silent */ }
  }, [API]);

  useEffect(() => {
    fetchUsers();
    fetchStats();
    fetchArticles();
  }, [fetchUsers, fetchStats, fetchArticles]);

  const handleSelectUser = async (u) => {
    setSelectedUser(u);
    setCreditAmount("");
    try {
      const { data } = await axios.get(`${API}/admin/users/${u.id}/queries`);
      setUserQueries(data.queries || []);
    } catch (e) { /* silent */ }
    // iter532 — fetch financial audit in parallel
    try {
      const { data: aud } = await axios.get(`${API}/admin/users/${u.id}/financial-audit`);
      setFinancialAudit(aud);
    } catch (e) { setFinancialAudit(null); }
    // On mobile, scroll to patient details
    setTimeout(() => {
      const details = document.querySelector('[data-testid="admin-patient-details"]');
      if (details && window.innerWidth < 1024) {
        details.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleAddCredits = async () => {
    if (!selectedUser || !creditAmount) return;
    const amount = parseInt(creditAmount);
    if (isNaN(amount) || amount === 0) return;
    try {
      const { data } = await axios.post(`${API}/admin/users/${selectedUser.id}/credits`, { credits: amount });
      toast.success(`${amount > 0 ? '+' : ''}${amount} кредита → ${selectedUser.name}`);
      setSelectedUser(prev => ({ ...prev, credits: data.new_credits }));
      setCreditAmount("");
      fetchUsers();
    } catch (e) { toast.error("Грешка при обновяване на кредитите"); }
  };

  const handleGiftSubscription = async () => {
    if (!selectedUser) return;
    setGiftLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/users/${selectedUser.id}/gift-subscription`, {
        plan: giftPlan, months: parseInt(giftMonths) || 1
      });
      toast.success(`${giftPlan.toUpperCase()} абонамент подарен на ${selectedUser.name} за ${giftMonths} мес.`);
      setSelectedUser(prev => ({
        ...prev,
        credits: (prev.credits || 0) + data.credits_added,
        subscription: { status: "active", plan: giftPlan, gifted: true }
      }));
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Грешка при подаряване на абонамент");
    } finally {
      setGiftLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Сигурни ли сте, че искате да изтриете ${userName}? Всички данни ще бъдат загубени.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success(`${userName} е изтрит/а успешно`);
      setSelectedUser(null);
      fetchUsers();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Грешка при изтриване");
    }
  };

  const handleRevokeSubscription = async () => {
    if (!selectedUser) return;
    if (!window.confirm(`Премахване на абонамента на ${selectedUser.name}?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${selectedUser.id}/subscription`);
      toast.success(`Абонаментът на ${selectedUser.name} е премахнат`);
      setSelectedUser(prev => ({ ...prev, subscription: { status: "expired", revoked: true } }));
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Грешка при премахване на абонамент");
    }
  };

  const handleExport = () => {
    const csvContent = users.map(u => {
      const p = u.profile || {};
      return [
        u.name, u.email,
        p.personal?.age || "", p.personal?.gender || "",
        p.body?.height || "", p.body?.weight || "",
        p.metrics?.bmi || "", p.metrics?.bmr || "",
        p.metrics?.tdee || "", p.lifestyle?.primaryGoal || ""
      ].join(",");
    }).join("\n");
    const header = "Name,Email,Age,Gender,Height,Weight,BMI,BMR,TDEE,Goal\n";
    const blob = new Blob([header + csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `patients_export_${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
  };

  const [broadcastText, setBroadcastText] = useState("");
  const [broadcasting, setBroadcasting] = useState(false);

  const handleBroadcast = async () => {
    if (!broadcastText.trim()) return;
    if (!window.confirm(`Изпращане на съобщение до ВСИЧКИ потребители?`)) return;
    setBroadcasting(true);
    try {
      const { data } = await axios.post(`${API}/messages/broadcast`, { text: broadcastText.trim(), subject: "Съобщение от администратор" });
      toast.success(`Съобщението е изпратено до ${data.recipients} потребители`);
      setBroadcastText("");
    } catch (e) { toast.error("Грешка при изпращане"); }
    finally { setBroadcasting(false); }
  };

  const filteredUsers = useMemo(() => users.filter(u =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  ), [users, searchTerm]);

  if (user?.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC] dark:bg-gray-900">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-gray-500">You don't have permission to access this page.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-[#FAFBFC]'}`}>
      <DashboardHeader activePanel="admin" setActivePanel={() => {}} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {STAT_CARDS.map(({ key, label, color, icon: Icon, rawLabel }) => (
            <Card key={key} className={isDark ? 'bg-gray-800 border-gray-700' : 'card-elevated border-0'}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{rawLabel ? label : t(label)}</p>
                    <p className="text-2xl font-bold" style={{ color }}>{stats[key]}</p>
                  </div>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                    <Icon className="w-4 h-4" style={{ color }} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Табове — iter1071: пренасят се на нов ред вместо да преливат.
            Лентата беше с hide-scrollbar, тоест на екран без допир нямаше
            как да се стигне до по-далечните табове. */}
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="admin-tabs-bar">
          <a href="/admin/errors"
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap bg-rose-500/15 text-rose-500 border border-rose-500/40"
            data-testid="admin-tab-errors">
            <ShieldAlert className="w-4 h-4" /> 🚨 Инбокс за грешки
          </a>
          {/* iter1091 — Прекият link до Пациенти (impersonation view). Изнесен като
              „бутон" защото беше скрит зад Users tab-a и хората не го намираха. */}
          <a href="/admin/patients"
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap bg-fuchsia-500/15 text-fuchsia-500 border border-fuchsia-500/40 hover:bg-fuchsia-500/25"
            data-testid="admin-tab-patients">
            <ScanEye className="w-4 h-4" /> 👁️ Виж като пациент
          </a>
          {/* iter1094 — Пряк линк до Impersonation Audit Log (GDPR compliance). */}
          <a href="/admin/audit-log"
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap bg-sky-500/15 text-sky-500 border border-sky-500/40 hover:bg-sky-500/25"
            data-testid="admin-tab-audit-log">
            <History className="w-4 h-4" /> 🕵️ Одит на impersonation
          </a>
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-gradient-to-r from-pink-500 to-violet-600 text-white shadow-sm shadow-violet-500/30'
                  : isDark ? 'bg-gray-800 text-gray-400 hover:text-white' : 'bg-white text-gray-500 hover:text-violet-700 hover:bg-violet-50 border border-gray-200'
              }`} data-testid={`admin-tab-${tab.key}`}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        {/* Broadcast Message */}
        <div className={`mb-4 p-3 rounded-xl border flex items-center gap-2 ${isDark ? 'bg-[hsl(223,47%,12%)] border-[hsl(223,47%,20%)]' : 'bg-white border-gray-100'}`} data-testid="broadcast-section">
          <MessageSquare className="w-4 h-4 text-[#0EA5E9] flex-shrink-0" />
          <Input value={broadcastText} onChange={e => setBroadcastText(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") handleBroadcast(); }}
            placeholder="Съобщение до всички потребители..."
            className={`flex-1 h-8 text-xs ${isDark ? 'bg-[hsl(223,47%,15%)] border-[hsl(223,47%,20%)] text-white' : ''}`}
            data-testid="broadcast-input" />
          <Button size="sm" className="h-8 text-xs bg-[#0EA5E9] hover:bg-[#0284C7] text-white"
            onClick={handleBroadcast} disabled={broadcasting || !broadcastText.trim()} data-testid="broadcast-btn">
            {broadcasting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Broadcast
          </Button>
        </div>

        {/* Tab Content */}
        {activeTab === "clinicians" ? (
          <ClinicianVerification />
        ) : activeTab === "onco_verify" ? (
          <OncologyVerificationsPanel />
        ) : activeTab === "clin_trials" ? (
          <ClinicianTrialRequestsPanel />
        ) : activeTab === "risk_stats" ? (
          <RiskStatsPanel />
        ) : activeTab === "articles" ? (
          <ArticlesManager
            API={API} isDark={isDark} articles={articles} fetchArticles={fetchArticles}
            editArticle={editArticle} setEditArticle={setEditArticle}
            articleForm={articleForm} setArticleForm={setArticleForm}
          />
        ) : activeTab === "cookbook" ? (
          <AdminCookbookPanel API={API} isDark={isDark} />
        ) : activeTab === "broadcasts" ? (
          <AdminBroadcastsPanel />
        ) : activeTab === "foods" ? (
          <FoodDatabaseManager API={API} isDark={isDark} />
        ) : activeTab === "nutrami" ? (
          <NutramiManager />
        ) : activeTab === "food_suggestions" ? (
          <FoodSuggestionsManager />
        ) : activeTab === "usda_foods" ? (
          <UsdaFoodsManager API={API} isDark={isDark} />
        ) : activeTab === "drug_registry" ? (
          <DrugRegistryManager API={API} isDark={isDark} />
        ) : activeTab === "landing" ? (
          <LandingCmsEditor API={API} isDark={isDark} />
        ) : activeTab === "seo" ? (
          <SeoManager API={API} />
        ) : activeTab === "analyses" ? (
          <AdminAnalysesPanel
            isDark={isDark} users={filteredUsers} searchTerm={searchTerm}
            setSearchTerm={setSearchTerm} API={API}
          />
        ) : activeTab === "security" ? (
          <AdminSecurityPanel />
        ) : activeTab === "compliance" ? (
          <ComplianceCenterWidget />
        ) : activeTab === "formula_audit" ? (
          <FormulaAuditLog />
        ) : activeTab === "seo_audit" ? (
          <SeoAuditPanel />
        ) : activeTab === "trials" ? (
          <TrialAnalyticsPanel />
        ) : activeTab === "faq" ? (
          <AdminFAQ API={API} isDark={isDark} />
        ) : activeTab === "promo" ? (
          <AdminPromoCodes API={API} isDark={isDark} />
        ) : activeTab === "ambassadors" ? (
          <AdminAmbassadors API={API} isDark={isDark} />
        ) : activeTab === "advisory_coi" ? (
          <AdvisoryCOIPanel />
        ) : activeTab === "health" ? (
          <AdminHealthDashboard API={API} />
        ) : activeTab === "inter_rater" ? (
          <InterRaterStudyPanel API={API} isDark={isDark} />
        ) : activeTab === "batch_pilot" ? (
          <BatchPilotRunner API={API} isDark={isDark} />
        ) : activeTab === "mollie" ? (
          <MolliePaymentsPanel API={API} isDark={isDark} />
        ) : activeTab === "bg_calib" ? (
          <BgCalibrationPanel API={API} isDark={isDark} />
        ) : activeTab === "site_kb" ? (
          <SiteKnowledgePanel />
        ) : activeTab === "marker_reports" ? (
          <AdminMarkerReports />
        ) : activeTab === "unmapped_kegg" ? (
          <AdminUnmappedConditions />
        ) : (
          <AdminUsersPanel
            isDark={isDark} t={t} users={filteredUsers} selectedUser={selectedUser}
            searchTerm={searchTerm} setSearchTerm={setSearchTerm}
            handleSelectUser={handleSelectUser} handleExport={handleExport}
            handleDeleteUser={handleDeleteUser} handleAddCredits={handleAddCredits}
            handleRevokeSubscription={handleRevokeSubscription}
            creditAmount={creditAmount} setCreditAmount={setCreditAmount}
            giftPlan={giftPlan} setGiftPlan={setGiftPlan}
            giftMonths={giftMonths} setGiftMonths={setGiftMonths}
            giftLoading={giftLoading} handleGiftSubscription={handleGiftSubscription}
            userQueries={userQueries} API={API} onUserUpdated={fetchUsers}
            financialAudit={financialAudit}
          />
        )}
      </main>

      <AppFooter />
      <ScrollToTop />
    </div>
  );
}
